/**
 * useSyncCharge
 *
 * Hook para o botão "Sincronizar agora" no modal de detalhes da cobrança.
 *
 * Fluxo:
 *   1. Chama RPC `request_single_charge_reconciliation` que valida ownership,
 *      aplica rate-limit server-side de 30s e zera last_reconciled_at.
 *   2. Invoca a edge function `financial-reconciliation` que fará a consulta
 *      real ao provider e atualizará o status da cobrança.
 *   3. Invalida cache de cobranças/detalhes ao final.
 *
 * Rate-limit local de 30s adicional só para UX (countdown no botão).
 *
 * RELEASE: 2026-06-17 v1.0.0 - Versão inicial.
 */
import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useSyncCharge');

const COOLDOWN_SECONDS = 30;

export function useSyncCharge(cobrancaId: string | undefined) {
  const queryClient = useQueryClient();
  const [cooldownLeft, setCooldownLeft] = useState(0);

  // Tick do countdown
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const t = setInterval(() => setCooldownLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldownLeft]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!cobrancaId) throw new Error('Cobrança inválida');

      // 1. Solicita reconciliação (server-side rate limit + zera last_reconciled_at)
      const { error: rpcError } = await supabase.rpc('request_single_charge_reconciliation', {
        p_cobranca_id: cobrancaId,
      });

      if (rpcError) {
        // Erro de cooldown vem com ERRCODE check_violation
        if (rpcError.message?.toLowerCase().includes('limite 30s') ||
            rpcError.code === '23514') {
          throw new Error('Aguarde alguns segundos antes de sincronizar novamente');
        }
        throw new Error(rpcError.message || 'Falha ao solicitar sincronização');
      }

      // 2. Dispara a edge function de reconciliação (best-effort)
      try {
        await supabase.functions.invoke('financial-reconciliation', { body: {} });
      } catch (err) {
        // O cron de 2 min cobre falhas transitórias — não bloqueia o usuário.
        logger.warn('Edge function reconciliation falhou (será reprocessada pelo cron)', { err });
      }

      return true;
    },
    onSuccess: () => {
      setCooldownLeft(COOLDOWN_SECONDS);
      toast.success('Sincronização solicitada — atualizando status...');
      queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobrancaId] });
      queryClient.invalidateQueries({ queryKey: ['cobrancas-paginated'] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const sync = useCallback(() => {
    if (cooldownLeft > 0) return;
    mutation.mutate();
  }, [cooldownLeft, mutation]);

  return {
    sync,
    cooldownLeft,
    isPending: mutation.isPending,
    canSync: !mutation.isPending && cooldownLeft === 0,
  };
}
